import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllRetailersWithWallet, setRetailerBlocked } from '../../services/apiService';

const TABS = [
  { key: 'all',           label: 'Alle' },
  { key: 'actief',        label: 'Actief' },
  { key: 'inactief',      label: 'Inactief' },
  { key: 'in_afwachting', label: 'Nieuw' },
];

export default function AdminShopsPage() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [toast, setToast] = useState(null);

  const loadShops = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const rows = await getAllRetailersWithWallet();
      setShops(rows);
    } catch (err) {
      setLoadError(err.message || 'Kon winkeliers niet laden');
      setShops([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShops();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const toggleStatus = async (shop) => {
    const nextActive = shop.status === 'geblokkeerd';
    try {
      // Blocking also pauses all of the shop's running deals immediately.
      const { paused } = await setRetailerBlocked(shop, !nextActive);
      showToast(
        nextActive
          ? `✅ ${shop.shopName} geactiveerd`
          : `🚫 ${shop.shopName} geblokkeerd — ${paused} ${paused === 1 ? 'deal' : 'deals'} gestopt`,
        nextActive ? 'success' : 'error'
      );
      await loadShops();
    } catch (err) {
      showToast(err.message || 'Actie mislukt', 'error');
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shops.filter((s) => {
      if (tab !== 'all' && (s.status || 'actief') !== tab) return false;
      if (!q) return true;
      return (
        (s.shopName || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q) ||
        (s.area || '').toLowerCase().includes(q) ||
        String(s.id || '').toLowerCase().includes(q) ||
        (s.locationCode || '').toLowerCase().includes(q)
      );
    });
  }, [shops, tab, query]);

  if (loading) {
    return (
      <div>
        <header className="admin-page-head">
          <div>
            <h1 className="admin-page-title">Winkeliers</h1>
            <p className="admin-page-sub">Laden…</p>
          </div>
        </header>
        <div className="admin__empty admin-card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <p style={{ color: '#6B7280', margin: 0 }}>Winkeliers laden…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        {toast && (
          <div className={`admin__toast admin__toast--${toast.type}`}>{toast.message}</div>
        )}
        <header className="admin-page-head">
          <div>
            <h1 className="admin-page-title">Winkeliers</h1>
            <p className="admin-page-sub">Fout bij laden</p>
          </div>
        </header>
        <div className="admin__empty admin-card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <span style={{ fontSize: 56, display: 'block' }}>⚠️</span>
          <h3 style={{ margin: '12px 0 6px', color: '#991B1B' }}>Kon winkeliers niet laden</h3>
          <p style={{ color: '#6B7280', margin: '0 0 18px' }}>{loadError}</p>
          <button className="admin-btn admin-btn--orange" onClick={loadShops}>
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }

  if (shops.length === 0) {
    return (
      <div>
        {toast && (
          <div className={`admin__toast admin__toast--${toast.type}`}>{toast.message}</div>
        )}
        <header className="admin-page-head">
          <div>
            <h1 className="admin-page-title">Winkeliers</h1>
            <p className="admin-page-sub">Nog geen accounts</p>
          </div>
        </header>
        <div className="admin__empty admin-card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <span style={{ fontSize: 56, display: 'block' }}>🏪</span>
          <h3 style={{ margin: '12px 0 6px', color: '#1A3E5C' }}>Nog geen winkeliers</h3>
          <p style={{ color: '#6B7280', margin: '0 0 18px' }}>
            Maak de eerste winkelier aan.
          </p>
          <button
            className="admin-btn admin-btn--orange"
            onClick={() => navigate('/admin/shops/new')}
          >
            ➕ Eerste winkelier aanmaken
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <div className={`admin__toast admin__toast--${toast.type}`}>{toast.message}</div>
      )}
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Winkeliers</h1>
          <p className="admin-page-sub">{shops.length} accounts in totaal</p>
        </div>
        <button
          className="admin-btn admin-btn--orange"
          onClick={() => navigate('/admin/shops/new')}
        >
          + Nieuwe winkelier
        </button>
      </header>

      <div className="admin-toolbar">
        <input
          className="admin-input admin-input--grow"
          type="search"
          placeholder="Zoek op naam, email, gebied of User ID…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={'admin-tab' + (tab === t.key ? ' admin-tab--active' : '')}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Winkelnaam</th>
              <th>Email</th>
              <th>Gebied</th>
              <th>Locatiecode</th>
              <th>User ID</th>
              <th>Status</th>
              <th>Deals zichtbaar</th>
              <th>Tokens</th>
              <th>Abonnement</th>
              <th>Lid sinds</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="admin-empty">Geen winkeliers gevonden</td></tr>
            )}
            {filtered.map((s) => {
              const status = s.status || 'actief';
              const isUnlim = s.subscription === 'unlimited';
              const dealCount = Number(s.dealCount || 0);
              return (
                <tr key={s.id || s.email}>
                  <td>
                    <button
                      className="admin-link"
                      onClick={() => navigate(`/admin/shops/${s.id}`)}
                    >
                      {s.shopName}
                    </button>
                  </td>
                  <td>{s.email}</td>
                  <td>{s.area}</td>
                  <td><span className="admin-mono">{s.locationCode || '—'}</span></td>
                  <td><span className="admin-mono">{s.id || '—'}</span></td>
                  <td>
                    <span className={`admin-badge admin-badge--${status}`}>
                      {status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    {status === 'actief' ? (
                      <strong>{dealCount}</strong>
                    ) : (
                      <span style={{ color: status === 'geblokkeerd' ? '#991B1B' : '#6B7280' }}>
                        0 zichtbaar
                      </span>
                    )}
                  </td>
                  <td>
                    {isUnlim
                      ? <span className="admin-badge admin-badge--unlimited">Unlimited</span>
                      : `${s.tokensAvailable ?? 0} / ${s.tokensPurchased ?? 0}`}
                  </td>
                  <td>
                    <span className={`admin-badge admin-badge--${isUnlim ? 'unlimited' : 'starter'}`}>
                      {isUnlim ? 'Unlimited' : 'Tokens'}
                    </span>
                  </td>
                  <td>{s.createdAt ? new Date(s.createdAt).toLocaleDateString('nl-NL') : '—'}</td>
                  <td>
                    <div className="admin-table__actions">
                      <button
                        className="admin-btn admin-btn--small admin-btn--ghost"
                        onClick={() => navigate(`/admin/shops/${s.id}`)}
                        title="Bewerk"
                      >
                        ✏️
                      </button>
                      {status === 'geblokkeerd' ? (
                        <button
                          className="admin-btn admin-btn--small"
                          style={{ background: '#16A34A' }}
                          onClick={() => toggleStatus(s)}
                          title="Activeer"
                        >
                          ✅
                        </button>
                      ) : (
                        <button
                          className="admin-btn admin-btn--small admin-btn--danger"
                          onClick={() => toggleStatus(s)}
                          title="Blokkeer"
                        >
                          🚫
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
